<template>
  <div class="layout-container">
    <!-- 二级路由出口：首页（Home）缓存 —— 打开页面只请求一次，切换任务栏不再重复请求 -->
    <keep-alive :include="cachedViews">
      <router-view />
    </keep-alive>
    <!-- 底部导航 -->
    <div class="bottom-nav">
      <div 
        v-for="item in navItems" 
        :key="item.path"
        class="nav-item" 
        :class="{ active: isActive(item.path) }"
        @click="navigateTo(item.path)"
      >
        <i :class="item.icon"></i>
        <span class="nav-label">
          {{ item.label }}
          <!-- 消息红标：显示在「消息」字样右上角 -->
          <span v-if="item.path === '/chats' && unreadTotal > 0" class="nav-badge">{{ unreadText }}</span>
        </span>
      </div>
    </div>
  </div>
</template>

<script>
import { getChatUnreadApi } from '@/api/chat'
import chatSocket from '@/utils/ws'

// 未读客服消息数轮询间隔（毫秒）：WebSocket 实时推送 + 轮询兜底
const UNREAD_POLL_MS = 20000

export default {
  name: 'Layout',
  data() {
    return {
      // 未读客服消息总数（底部导航「消息」红标）
      unreadTotal: 0,
      unreadTimer: null,
      unreadRefreshTimer: null,
      // 需要缓存的页面组件名：首页 / 分类（打开页面只请求一次，切换底部任务栏不再重新请求）
      cachedViews: ['Home', 'Category'],
      navItems: [
        { path: '/home', label: '首页', icon: 'fas fa-house' },
        { path: '/category', label: '分类', icon: 'fas fa-th-large' },
        // 「购物车」已移动到「我的 - 我的服务」中，此处改为「聊天」：展示各商家客服入口
        { path: '/chats', label: '消息', icon: 'fas fa-comments' },
        { path: '/user', label: '我的', icon: 'fas fa-user' }
      ]
    }
  },
  computed: {
    isLogin () {
      return !!this.$store.getters.token
    },
    // 红标最多显示 99+
    unreadText () {
      return this.unreadTotal > 99 ? '99+' : String(this.unreadTotal)
    }
  },
  watch: {
    // 登录 / 退出登录后立即刷新红标
    isLogin () {
      this.refreshUnread()
    },
    // 路由变化（进入聊天页已读、返回列表等）后刷新红标
    '$route.path' () {
      this.refreshUnread()
    }
  },
  created () {
    this.refreshUnread()
  },
  mounted () {
    this.bindSocket()
    this.startPolling()
    // 切回前台时刷新一次，保证红标及时准确
    this._onVisible = () => {
      if (document.visibilityState === 'visible') {
        this.refreshUnread()
      }
    }
    document.addEventListener('visibilitychange', this._onVisible)
  },
  beforeDestroy () {
    this.stopPolling()
    this.unbindSocket()
    if (this.unreadRefreshTimer) {
      clearTimeout(this.unreadRefreshTimer)
      this.unreadRefreshTimer = null
    }
    if (this._onVisible) {
      document.removeEventListener('visibilitychange', this._onVisible)
      this._onVisible = null
    }
  },
  methods: {
    isActive(path) {
      return this.$route.path === path || this.$route.path.startsWith(path + '/')
    },
    navigateTo(path) {
      if (this.$route.path !== path) {
        this.$router.push(path)
      } else {
        // 已在该 Tab：再点一次时刷新红标，保证未读数最新
        this.refreshUnread()
      }
    },
    // ==================== 未读客服消息红标 ====================
    async refreshUnread () {
      if (!this.isLogin) {
        this.unreadTotal = 0
        return
      }
      try {
        const { data } = await getChatUnreadApi()
        this.unreadTotal = Number((data && data.total) || 0)
      } catch (e) {
        // 静默失败：轮询接口不打扰用户
      }
    },
    // WebSocket：客服发来新消息 / 已读变化时聚合刷新（800ms 内只刷新一次）
    bindSocket () {
      this.unbindSocket()
      this._onWsMessage = (payload) => {
        const msg = payload && payload.message
        if (!msg || Number(msg.sender_type) !== 2) return
        this.scheduleRefresh()
      }
      this._onWsRead = () => this.scheduleRefresh()
      this._onWsOpen = () => this.refreshUnread()
      chatSocket.on('message', this._onWsMessage)
      chatSocket.on('read', this._onWsRead)
      chatSocket.on('open', this._onWsOpen)
    },
    unbindSocket () {
      if (this._onWsMessage) chatSocket.off('message', this._onWsMessage)
      if (this._onWsRead) chatSocket.off('read', this._onWsRead)
      if (this._onWsOpen) chatSocket.off('open', this._onWsOpen)
      this._onWsMessage = null
      this._onWsRead = null
      this._onWsOpen = null
    },
    scheduleRefresh () {
      if (this.unreadRefreshTimer) return
      this.unreadRefreshTimer = setTimeout(() => {
        this.unreadRefreshTimer = null
        this.refreshUnread()
      }, 800)
    },
    startPolling () {
      this.stopPolling()
      this.unreadTimer = setInterval(() => {
        this.refreshUnread()
      }, UNREAD_POLL_MS)
    },
    stopPolling () {
      if (this.unreadTimer) {
        clearInterval(this.unreadTimer)
        this.unreadTimer = null
      }
    }
  }
}
</script>

<style lang="less" scoped>
.layout-container {
  min-height: 100vh;
  display: flex;
  flex-direction: column;

  .bottom-nav {
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    background: #ffffff;
    border-top: 1px solid #edf2f7;
    display: flex;
    justify-content: space-around;
    align-items: center;
    height: 50px;
    z-index: 999;

    .nav-item {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      font-size: 11px;
      color: #8e8e93;
      cursor: pointer;
      padding: 4px 12px;
      transition: color 0.2s;
      height: 100%;

      i {
        font-size: 22px;
        margin-bottom: 2px;
        color: #8e8e93;
        transition: color 0.2s;
      }

      .nav-label {
        position: relative;
        font-size: 11px;
        letter-spacing: 0.2px;

        // 未读客服消息红标：显示在「消息」字样右上角
        .nav-badge {
          position: absolute;
          top: -8px;
          right: -14px;
          min-width: 16px;
          height: 16px;
          line-height: 16px;
          padding: 0 4px;
          border-radius: 8px;
          background: #ff3b30;
          color: #fff;
          font-size: 10px;
          font-weight: 600;
          text-align: center;
          box-shadow: 0 1px 4px rgba(255, 59, 48, 0.4);
        }
      }

      span {
        font-size: 11px;
        letter-spacing: 0.2px;
      }

      &.active {
        color: #ff6b35;

        i {
          color: #ff6b35;
        }
      }

      &:hover {
        color: #ff6b35;

        i {
          color: #ff6b35;
        }
      }
    }
  }
}
</style>